import React from 'react';
import styled from 'styled-components/macro';
import {
  isOverdue,
  isThisWeek,
  isToday,
  isTomorrow
} from '../../../utils/dateUtils';
import FilledClockIcon from '@material-ui/icons/WatchLater';
import OutlinedClockIcon from '@material-ui/icons/AccessTime';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  priorityToDisplayMap,
  statusToDisplayTextMap
} from '../engine/TasksTabEngine';
import { StatusChip } from './TasksTabView';
import formatDate from '../../../utils/formatDate';
import DataGridEngine from '../../../internal-components/data-grid/engine/DataGridEngine';
import Button from '../../Button';
import EditTaskDialogEngine from '../engine/EditTaskDialogEngine';
import EndUserTaskFiltersEngine from '../engine/EndUserTaskFiltersEngine';

const TABLE_COLUMN_DEFINITIONS = [
  { id: 'title', display: 'Task', width: '5fr' },
  { id: 'status', display: 'Status', width: '1fr' },
  { id: 'formattedPriority', display: 'Priority', width: '1fr' },
  { id: 'formattedDueDate', display: 'Due date', width: '1fr' },
  { id: 'controls', display: '', width: '1fr', disableSort: true }
];

const getHeaderStyle = columnIndex => {
  let style = 'font-size: 12px; weight: 600px; background-color: #fbfbfb;';
  if (columnIndex === 0) {
    style += 'border-radius: 8px 0 0 0';
  } else if (columnIndex === TABLE_COLUMN_DEFINITIONS.length - 1) {
    style += 'border-radius: 0 8px 0 0';
  } else {
    style += 'border-radius: 0';
  }
  return style;
};

const TimeWord = styled.span`
  && {
    line-height: 1;
    vertical-align: middle;
  }
`;
const TimeWordRed = styled.span`
  && {
    color: #df8e82;
    font-weight: ${({ $heavy }) => ($heavy ? '600' : '500')};
    line-height: 1;
    vertical-align: middle;
  }
`;
const TodayClock = styled(FilledClockIcon)`
  && {
    color: #df8e82;
    font-size: 15px;
    vertical-align: middle;
  }
`;
const TomorrowClock = styled(FilledClockIcon)`
  && {
    color: #df8e82;
    font-size: 15px;
    vertical-align: middle;
  }
`;
const ThisWeekClock = styled(OutlinedClockIcon)`
  && {
    color: #fdb259;
    font-size: 15px;
    vertical-align: middle;
  }
`;

const StyledButton = styled(Button)`
  font-size: 10px !important;
`;

const ClearFiltersButton = styled(Button)`
  && {
    color: #000;
    font-size: 14px;
    font-weight: 500;
    margin-right: 1rem;
  }
`;

const FilterIcon = styled(FilterListIcon)`
  && {
    ${({ $currentlyFiltered }) => ($currentlyFiltered ? 'color: #4C84ff;' : '')}
    font-size: 22px;
    margin-right: 1rem;
    vertical-align: middle;
  }
`;

const TopControls = styled.div`
  align-items: center;
  display: flex;
`;

const TasksTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const FilterContainer = styled.div`
  flex: 1;
  text-align: right;
`;

const TaskTitle = styled.div`
  font-weight: 600;
  margin: 1rem 0;
`;

const TaskDescription = styled.div`
  margin-bottom: 1rem;
`;

const EndUserTaskTableView = props => {
  const columns = TABLE_COLUMN_DEFINITIONS.map((colDef, i) => {
    const style = getHeaderStyle(i);
    return { ...colDef, customCSS: style };
  });
  const rows = props.sortedRows.map(one => {
    let formattedDueDate = one.dueDate
      ? formatDate(+one.dueDate, false, 'DD/MM/YYYY', true)
      : '';
    if (isToday(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <TodayClock /> <TimeWordRed>Today</TimeWordRed>
        </div>
      );
    } else if (isTomorrow(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <TomorrowClock /> <TimeWord>Tomorrow</TimeWord>
        </div>
      );
    } else if (isThisWeek(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <ThisWeekClock /> <TimeWord>This week</TimeWord>
        </div>
      );
    } else if (isOverdue(one.dueDate)) {
      formattedDueDate = (
        <div>
          {formattedDueDate}
          <br />
          <TimeWordRed $heavy>! Overdue</TimeWordRed>
        </div>
      );
    }
    const formattedPriority = priorityToDisplayMap[one.priority];
    return {
      ...one,
      title: (
        <div>
          <TaskTitle>{one.title}</TaskTitle>
          <TaskDescription>{one.description}</TaskDescription>
        </div>
      ),
      formattedDueDate,
      formattedPriority,
      controls: (
        <StyledButton onClick={() => props.clickedTask(one)}>
          Manage task
        </StyledButton>
      ),
      status: (
        <StatusChip
          size="small"
          label={statusToDisplayTextMap[one.status]}
          $status={one.status}
          color="primary"
        />
      ),
      customCellCSS:
        'font-size: 12px; justify-content: center; height: initial; min-height: 60px;'
    };
  });
  return (
    <>
      <TopControls>
        <TasksTitle>Your assigned tasks</TasksTitle>
        <FilterContainer>
          {props.currentlyFiltered ? (
            <ClearFiltersButton onClick={props.clearFilters} variant="text">
              Clear filters
            </ClearFiltersButton>
          ) : (
            ''
          )}
          <FilterIcon $currentlyFiltered={props.currentlyFiltered} />
          <EndUserTaskFiltersEngine
            counts={props.counts}
            filtersChangedCallback={props.filtersChangedCallback}
            passedFilters={props.passedFilters}
          />
        </FilterContainer>
      </TopControls>
      <DataGridEngine
        columns={columns}
        rows={rows}
        customCSS="border-radius: 8px; border: 1px solid #c9c9c9;"
        sort={props.sortObject}
        onSortChange={props.onSortChange}
        noRecordsText={
          props.currentlyFiltered
            ? 'No tasks match filters.'
            : 'No tasks added.'
        }
      />
      <EditTaskDialogEngine
        key={props.taskBeingEdited?.id + props.refreshSeed}
        canReassign={props.canReassign}
        endUserDialog
        editedTaskCallback={props.editedTaskCallback}
        open={props.isEditTaskDialogOpen}
        taskBeingEdited={props.taskBeingEdited}
        closeEditTaskDialog={props.closeEditTaskDialog}
      />
    </>
  );
};

export default EndUserTaskTableView;
